<template>
  <div class="mt-1">
    <validation-observer ref="standardFormObserver">
      <b-form>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Zone Number" label-for="zoneNumber">
              <validation-provider #default="{ errors }" name="Zone Number"
                rules="required|numeric|min_value:1|max_value:999">
                <b-form-input id="zoneNumber" v-model="zoneForm.zoneNumber" type="text"
                  :state="errors.length > 0 ? false : null"
                  :disabled="isEditing || isSaving || !standardFields.zoneNumber" maxlength="3" placeholder="001" />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Zone Name" label-for="zoneName">
              <validation-provider #default="{ errors }" name="Zone Name" rules="required|max:32">
                <b-form-input id="zoneName" v-model="zoneForm.zoneName" type="text"
                  :state="errors.length > 0 ? false : null" :disabled="isSaving || !standardFields.zoneName"
                  maxlength="32" @input="zoneForm.zoneName = zoneForm.zoneName.toUpperCase()" />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Type" label-for="zoneType">
              <validation-provider #default="{ errors }" name="Type" rules="required">
                <b-form-select id="zoneType" v-model="zoneForm.zoneType" :options="typeOptions"
                  :state="errors.length > 0 ? false : null" :disabled="isSaving || !standardFields.zoneType" />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Area" label-for="areaNumber">
              <validation-provider #default="{ errors }" name="Area">
                <b-form-select id="areaNumber" v-model="zoneForm.areaNumber" :options="formattedAreasOptions"
                  :state="errors.length > 0 ? false : null" :disabled="isSaving || !standardFields.areaNumber" />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Follow Area" label-for="followArea">
              <b-form-input id="followArea" v-model="zoneForm.followArea" type="text" maxlength="5"
                :disabled="isSaving || !standardFields.followArea" />
              <b-form-text>Format: 1-32</b-form-text>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Armed Areas" label-for="armedAreas">
              <b-form-input id="armedAreas" v-model="zoneForm.armedAreas" type="text"
                :disabled="isSaving || !standardFields.armedAreas" />
              <b-form-text>Format: 1-32</b-form-text>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group :label="$t('dmp.zoneInformation.expanderSerialNumber')" label-for="expanderSerialNumber">
              <b-form-input id="expanderSerialNumber" v-model="zoneForm.expanderSerialNumber" type="text" maxlength="8"
                disabled />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group :label="$t('dmp.zoneInformation.zoneLocation')" label-for="zoneLocation">
              <b-form-input id="zoneLocation" v-model="zoneForm.zoneLocation" type="text" maxlength="32" />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { ref, computed, toRefs, getCurrentInstance } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import store from "@/store";
import { ZONE_TYPE_OPTIONS } from "./useZoneConfig";
import { useZoneFieldsEnabled } from "./useZoneFieldsEnabled";

export default {
  name: "ZoneStandard",
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    zoneForm: {
      type: Object,
      required: true,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const standardFormObserver = ref(null);

    const typeOptions = ZONE_TYPE_OPTIONS;

    const areasOptions = computed(() => {
      return store.getters["dmp_panels/getAreasList"];
    });

    const formattedAreasOptions = computed(() => {
      const areas = areasOptions.value;
      const options = [];

      if (!Array.isArray(areas)) {
        for (let i = 1; i <= 32; i++) {
          options.push({
            value: i,
            text: `${i} - ${proxy.$t("dmp.zoneInformation.unassigned")}`,
          });
        }
        return options;
      }

      const areaMap = new Map(areas.map((a) => [a.id, a]));
      options.push({
        value: 0,
        text: "——",
      });
      for (let i = 1; i <= 32; i++) {
        if (areaMap.has(i)) {
          const area = areaMap.get(i);
          options.push({
            value: area.id,
            text: `${area.id} - ${area.name}`,
          });
        } else {
          options.push({
            value: i,
            text: `${i} - ${proxy.$t("dmp.zoneInformation.unassigned")}`,
          });
        }
      }

      return options;
    });

    // Get field enablement logic
    const { zoneForm } = toRefs(props);
    const zoneType = computed(() => zoneForm.value.zoneType);
    const { standardFields } = useZoneFieldsEnabled(zoneType);


    return {
      standardFormObserver,
      typeOptions,
      formattedAreasOptions,
      standardFields,
    };
  },
};
</script>
